/**
 * frontend/src/lib/archiveApi.js
 * SoundDAO Sprint 6A — client for the Dig (Archive.org) and built-playlist APIs.
 *
 * Every call resolves to the parsed JSON body or throws an ApiError carrying the HTTP
 * status and the backend's `code` (e.g. 'ARCHIVE_UNAVAILABLE', 'PLAYLIST_FULL').
 */

import { authFetch } from './authFetch';

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export class ApiError extends Error {
  constructor(message, { status, code, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code || null;
    this.body = body || null;
  }
}

export const ARCHIVE_SLOW_MESSAGE = 'The Internet Archive is slow right now — try again in a minute.';

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const opts = { method, headers: {} };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  let res;
  try {
    res = await (auth ? authFetch : fetch)(`${API_BASE}${path}`, opts);
  } catch (err) {
    throw new ApiError('Network error — check your connection and try again.', { status: 0 });
  }
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const code = data?.code || null;
    const message = code === 'ARCHIVE_UNAVAILABLE' ? ARCHIVE_SLOW_MESSAGE : (data?.error || `Request failed (${res.status})`);
    throw new ApiError(message, { status: res.status, code, body: data });
  }
  return data;
}

const enc = encodeURIComponent;

// ── Dig ───────────────────────────────────────────────────────────────────────
export const digSearch = ({ q, source = 'etree', page = 1 }) =>
  request(`/api/dig/search?q=${enc(q)}&source=${enc(source)}&page=${enc(page)}`);
export const digItem   = (identifier) => request(`/api/dig/item/${enc(identifier)}`);
export const digArtist = (id) => request(`/api/dig/artist/${enc(id)}`);

// ── Built playlists ───────────────────────────────────────────────────────────
export const myPlaylists     = () => request('/api/built-playlists/mine');
export const createPlaylist  = (fields) => request('/api/built-playlists', { method: 'POST', body: fields });
export const updatePlaylist  = (id, fields) => request(`/api/built-playlists/${enc(id)}`, { method: 'PATCH', body: fields });
export const deletePlaylist  = (id) => request(`/api/built-playlists/${enc(id)}`, { method: 'DELETE' });
export const addTrack        = (id, identifier, fileName) =>
  request(`/api/built-playlists/${enc(id)}/tracks`, { method: 'POST', body: { identifier, fileName } });
export const removeTrack     = (id, trackId) => request(`/api/built-playlists/${enc(id)}/tracks/${enc(trackId)}`, { method: 'DELETE' });
export const reorderTracks   = (id, trackIds) => request(`/api/built-playlists/${enc(id)}/order`, { method: 'PATCH', body: { trackIds } });
export const sharePlaylist   = (id) => request(`/api/built-playlists/${enc(id)}/share`, { method: 'POST' });
export const publicPlaylist  = (slug) => request(`/api/built-playlists/public/${enc(slug)}`, { auth: false });

// ── Helpers ───────────────────────────────────────────────────────────────────
export function formatDuration(sec) {
  if (sec === null || sec === undefined || !Number.isFinite(Number(sec))) return '';
  const s = Math.max(0, Math.round(Number(sec)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

export function formatShowDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** Tracks from an item response → player queue entries. `from` = where playback started
 *  (analytics track_played.source: 'dig' | 'playlist' | 'public_playlist' | 'claim_page'). */
export function itemToQueue(item, from = 'dig') {
  return (item.tracks || []).map((t) => ({
    key:        `${item.identifier}/${t.fileName}`,
    title:      t.title,
    artist:     (item.artists || []).map((a) => a.displayName).join(' / ') || item.creator || '',
    streamUrl:  t.streamUrl,
    archiveUrl: item.archiveUrl,
    identifier: item.identifier,
    from,
  }));
}

/** Tracks from a playlist response → player queue entries. */
export function playlistToQueue(playlist, from = 'playlist') {
  return (playlist.tracks || []).map((t) => ({
    key:        `${t.identifier}/${t.fileName}`,
    title:      t.title || t.fileName,
    artist:     t.artistDisplay || '',
    streamUrl:  t.streamUrl,
    archiveUrl: t.archiveUrl,
    identifier: t.identifier,
    from,
  }));
}

// ── Nominations (Prompt 6) ────────────────────────────────────────────────────
export const nominateArtist  = (artistId) => request(`/api/dig/artist/${enc(artistId)}/nominate`, { method: 'POST' });
export const myNominations   = () => request('/api/dig/nominations/mine');

// ── Claims (Prompt 6) ─────────────────────────────────────────────────────────
export const claimPage   = (artistId) => request(`/api/claims/artist/${enc(artistId)}`, { auth: false });
export const founderPicks = () => request('/api/dig/founder-picks');
export const claimActivity = (artistId) => request(`/api/claims/artist/${enc(artistId)}/activity`, { auth: false });
export const startClaim  = (artistId) => request('/api/claims', { method: 'POST', body: { artistId } });
export const getClaim    = (claimId) => request(`/api/claims/${enc(claimId)}`);
export const checkClaim  = (claimId) => request(`/api/claims/${enc(claimId)}/check`, { method: 'POST' });

/** The artist a signed-out visitor is claiming, carried through magic-link sign-in. */
export const PENDING_CLAIM_KEY = 'sounddao_claim_artist_id';
export function getPendingClaim() {
  try { return localStorage.getItem(PENDING_CLAIM_KEY) || null; } catch { return null; }
}
export function setPendingClaim(artistId) {
  try {
    localStorage.setItem(PENDING_CLAIM_KEY, artistId);
    // Claims pay nominators only on verification — never a referral recruit for this signup.
    localStorage.removeItem('sounddao_referral_code');
  } catch { /* storage unavailable */ }
}
export function clearPendingClaim() {
  try { localStorage.removeItem(PENDING_CLAIM_KEY); } catch { /* storage unavailable */ }
}
